import { Select } from "@JohnLewisPartnership/jl-design-system/dist/components/select/Select";
import styles from "./Sort.module.scss";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import classnames from "classnames/bind";
import { useRouter } from "next/router";
import { ChangeEvent, useEffect, useState } from "react";
import { sortOptions } from "./SortOptions";
import { SORT_OPTION_CHANGED } from "@/utils/events";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { StandardProps } from "@/types/types";
import { getAnalyticsPageType } from "@/utils/setJLData";
import { useFeatureFlags } from "@/flags/flags";
import { useRouteParams } from "@/hooks/useRouteParams";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { ChevronDownOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronDownOutlined";

const cx = classnames.bind(styles);

export function Sort({
  productListingData,
  configProps,
}: Pick<StandardProps, "productListingData" | "configProps">) {
  const { queryWithoutPageOrChunk, pageType, path } = useRouteParams();
  const { isTabletEnhancementsFlagOn } = useFeatureFlags();
  const router = useRouter();

  const [isInteractive, setIsInteractive] = useState(false);

  useEffect(function enableInteractiveSort() {
    setIsInteractive(true);
  }, []);

  const existingSort = queryWithoutPageOrChunk.sortBy;
  const displayChevron = !!configProps?.inline;

  const handleSort = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    const sortBy = e.target.value;

    dispatchLegacyAnalyticsEvent(SORT_OPTION_CHANGED, {
      pagetype: getAnalyticsPageType(pageType, productListingData.results),
      sort: sortBy,
    });

    router.push({
      pathname: path,
      query: sanitiseQueryParams({ ...queryWithoutPageOrChunk, sortBy }),
    });
  };

  return (
    <div
      className={cx("sort", {
        sort__tabletEnhancements: isTabletEnhancementsFlagOn?.value,
        sort__inline: configProps?.inline,
      })}
    >
      <form action={path} method="GET" className={cx("sort__form")}>
        {Object.entries(queryWithoutPageOrChunk).map(
          ([name, value]) =>
            name !== "sortBy" && (
              <input key={name} name={name} value={value} type="hidden" />
            ),
        )}
        <div className={cx("sort__select")} data-testid="sort-by">
          <label className={styles.visuallyHidden} htmlFor="sortBy">
            Sort by
          </label>
          <Select
            id="sortBy"
            name="sortBy"
            labelText=""
            defaultValue={
              (configProps?.sort as string) ||
              existingSort ||
              sortOptions[0]?.label
            }
            onChange={handleSort}
            data-testid="sortBySelect"
            className={cx("sort__select__input")}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {displayChevron && (
            <div className={styles.sort__chevron}>
              <ChevronDownOutlined size="small" />
            </div>
          )}
        </div>
        <PrimaryButton
          type="submit"
          className={cx("sort__submit", {
            "sort__submit--visible": !isInteractive,
          })}
          labelText="Go"
        />
      </form>
    </div>
  );
}
